import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import Papa from 'papaparse';

const text = value => String(value == null ? '' : value).trim();
const keyOf = row => row.key || JSON.stringify([text(row.iBuildingID), text(row.location).normalize('NFKC').toLowerCase()]);
function errorMessage(error) {
  const response = error?.response || error;
  return response?.data?.error || response?.data?.message || error?.message || 'The location request failed. Refresh before retrying.';
}

// Identical presentation adapter in the two separately built apps. `client` is
// each app's existing authenticated Axios client; the list itself lives ONLY
// in sharedLocationCatalogService on the server, never in this component.
export default function BuildingLocationsEditor({ scope, client, onRowsChanged }) {
  const account = text(scope?.iAccountID); const building = text(scope?.iBuildingID);
  const params = useMemo(() => ({ iAccountID: account, iBuildingID: building }), [account, building]);
  const scopeKey = JSON.stringify([account, building]);
  const currentScope = useRef(scopeKey); currentScope.current = scopeKey;
  const callback = useRef(onRowsChanged); callback.current = onRowsChanged;
  const mounted = useRef(false); const requestNumber = useRef(0); const writing = useRef(false);
  const input = useRef(null);
  const [state, setState] = useState({ scope: '', rows: [], loading: false });
  const [drafts, setDrafts] = useState({}); const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [message, setMessage] = useState('');
  const valid = Boolean(account && building);
  const rows = state.scope === scopeKey ? state.rows : [];
  const loading = state.loading || state.scope !== scopeKey;
  const current = () => mounted.current && currentScope.current === scopeKey;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; requestNumber.current += 1; }; }, []);

  const load = useCallback(async () => {
    const version = ++requestNumber.current;
    if (!account || !building) { setState({ scope: scopeKey, rows: [], loading: false }); return; }
    setState(previous => ({ scope: scopeKey, rows: previous.scope === scopeKey ? previous.rows : [], loading: true }));
    try {
      const { data } = await client.get('/userlocations', { params });
      if (!Array.isArray(data)) throw new Error('The server did not return a location list.');
      if (!mounted.current || currentScope.current !== scopeKey || requestNumber.current !== version) return;
      const next = data.filter(row => text(row.iBuildingID) === building && text(row.location));
      setState({ scope: scopeKey, rows: next, loading: false });
      callback.current?.(next);
    } catch (err) {
      if (!mounted.current || currentScope.current !== scopeKey || requestNumber.current !== version) return;
      setState({ scope: scopeKey, rows: [], loading: false });
      callback.current?.([]);
      setError(errorMessage(err));
    }
  }, [account, building, scopeKey, params, client]);

  useEffect(() => {
    setDrafts({}); setNewName(''); setError(''); setMessage('');
    void load();
    const onFocus = () => { if (!writing.current) void load(); };
    window.addEventListener('focus', onFocus);
    return () => { requestNumber.current += 1; window.removeEventListener('focus', onFocus); };
  }, [load]);

  async function run(work, successText) {
    if (writing.current || !valid) return;
    writing.current = true; setBusy(true); setError(''); setMessage('');
    try {
      await work();
      if (current()) { setDrafts({}); setNewName(''); setMessage(successText); }
    } catch (err) { if (current()) setError(errorMessage(err)); }
    finally {
      // Even a partially completed CSV import is re-read from the server.
      if (current()) {
        await load();
        window.dispatchEvent(new CustomEvent('schoolshield:locations-changed', { detail: params }));
      }
      // Release the busy state even when the parent switched buildings while
      // the old request was finishing; never apply old rows to the new scope.
      if (mounted.current) setBusy(false);
      writing.current = false;
    }
  }
  function save(row) {
    const location = text(drafts[keyOf(row)]);
    if (!location || row.readOnly || !row.locationID) return;
    return run(() => client.put(`/userlocations/${row.locationID}`, { ...params, location, expectedLocation: row.location }), 'Location saved.');
  }
  function remove(row) {
    if (row.readOnly || !row.locationID || !window.confirm(`Delete shared location “${row.location}” from this building?`)) return;
    return run(() => client.delete(`/userlocations/${row.locationID}`, { data: { ...params, expectedLocation: row.location } }), 'Location deleted.');
  }
  function add() {
    if (!text(newName)) return;
    return run(() => client.post('/userlocations', { ...params, location: text(newName) }), 'Location saved.');
  }
  function exportCsv() {
    const csv = Papa.unparse({ fields: ['location'], data: rows.map(row => [row.location]) }, { quotes: true, escapeFormulae: true, newline: '\r\n' });
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a'); link.href = url;
    link.download = `shared_locations_${building.replace(/[^a-z0-9_-]/gi, '_')}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function importCsv(event) {
    const file = event.target.files?.[0]; if (!file) return;
    event.target.value = '';
    return run(async () => {
      if (file.size > 2 * 1024 * 1024) throw new Error('Use a CSV no larger than 2 MB.');
      const parsed = Papa.parse((await file.text()).replace(/^\uFEFF/, ''), { skipEmptyLines: 'greedy' });
      if (parsed.errors.length) throw new Error(`CSV could not be read: ${parsed.errors[0].message}`);
      const values = parsed.data;
      if (!values.length || values[0].length !== 1 || text(values[0][0]).toLowerCase() !== 'location') throw new Error('Use one CSV column with the header location.');
      if (values.length > 5001) throw new Error('Use at most 5,000 locations per CSV.');
      const names = values.slice(1).map((row, index) => {
        if (row.length !== 1) throw new Error(`CSV row ${index + 2} must have exactly one field.`);
        let value = text(row[0]);
        if (/^'[=+@\-\t\r']/.test(value)) value = value.slice(1);
        if (!value || Array.from(value).length > 255 || /[\u0000-\u001f\u007f]/.test(value)) throw new Error(`CSV row ${index + 2} needs a valid location name (1–255 characters).`);
        return value;
      });
      let saved = 0;
      for (const location of [...new Set(names)]) {
        if (!current()) throw new Error('The building changed; the remaining CSV rows were not submitted.');
        try { await client.post('/userlocations', { ...params, location }); saved += 1; }
        catch (err) { throw new Error(`${saved} location(s) processed. Import stopped: ${errorMessage(err)}`); }
      }
    }, 'CSV processed. Existing names were reused; the list below shows the saved result.');
  }

  return <section aria-label="Shared building locations">
    <p className="small text-muted mb-2">{rows[0]?.buildingName || (building === '-99' ? 'District' : 'Selected building')} only. Shared locations and current homerooms use one list across User Management, Entry/Exit, Verify and Incident Alert.</p>
    {!valid && <Alert variant="warning">Select a building first. District does not include every school.</Alert>}
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {message && <Alert variant="success" role="status">{message}</Alert>}
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Button size="sm" variant="outline-primary" onClick={() => { setError(''); load(); }} disabled={!valid || busy || loading}>Refresh list</Button>
      <Button size="sm" variant="outline-primary" onClick={exportCsv} disabled={!rows.length || busy || loading}>Export CSV</Button>
      <Button size="sm" variant="outline-secondary" onClick={() => input.current?.click()} disabled={!valid || busy || loading}>Import CSV</Button>
      <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={importCsv} />
    </div>
    {(busy || (loading && valid)) && <div role="status" className="mb-2"><Spinner size="sm" animation="border" /> {busy ? 'Saving…' : 'Loading locations…'}</div>}
    <Table size="sm" striped bordered responsive>
      <thead><tr><th>Location</th><th>Actions</th></tr></thead>
      <tbody>{rows.map(row => {
        const key = keyOf(row); const draft = drafts[key]; const changed = draft !== undefined && draft !== row.location;
        return <tr key={key}><td>
          {row.readOnly ? <><span>{row.location}</span><div className="small text-muted">Homeroom — maintained in Student History / source import</div></> : <Form.Control aria-label={`Location ${row.location}`} size="sm" value={draft ?? row.location} maxLength={255} disabled={busy || loading} onChange={event => { const value = event.target.value; setDrafts(old => ({ ...old, [key]: value })); }} />}
        </td><td>
          {row.readOnly ? <span className="small text-muted">Read-only source</span> : <div className="d-flex gap-1 flex-wrap">
            {changed && <><Button size="sm" onClick={() => save(row)} disabled={busy || loading || !text(draft)}>Save</Button><Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setDrafts(old => { const next = { ...old }; delete next[key]; return next; })}>Cancel</Button></>}
            <Button size="sm" variant="outline-danger" disabled={busy || loading} onClick={() => remove(row)}>Delete</Button>
          </div>}
        </td></tr>;
      })}
      {!rows.length && !loading && <tr><td colSpan={2}>No locations or current homerooms found for this building.</td></tr>}
      <tr><td><Form.Control size="sm" aria-label="New shared location" placeholder="New location" maxLength={255} value={newName} disabled={!valid || busy || loading} onChange={event => setNewName(event.target.value)} /></td>
        <td><Button size="sm" variant="success" disabled={!valid || busy || loading || !text(newName)} onClick={add}>Add</Button></td></tr>
      </tbody>
    </Table>
    <div className="small text-muted">Adding a location does not assign users or students. Imported homerooms are selectable everywhere but cannot be renamed or deleted here. Existing assignments and movement history are preserved.</div>
  </section>;
}
